import * as React from "react";
import { FormattedMessage, useIntl } from "@/i18n/i18n-client";
import { Field, Form } from "react-final-form";
import Asterisk from "@/shared/globals/UiElements/Asterisk";
import { useStore } from "@/lib/storeData/StoreContext";
import { PrimaryButton } from "@/components/Button";
import { Envelope } from "@/assets/Icons/Envelope";
import { Marker } from "@/assets/Icons/Marker";
import { Mobile } from "@/assets/Icons/Mobile";
import { FlexRow, H3, H5, Input, Label, TextArea } from "@/shared/globals";
import {
  Container,
  StyledH4,
  StyledSection,
  ContactForm,
  RequiredSpan,
  ContactInfo,
  AddressBook,
  InfoHolder,
  Icon,
  Info,
  StyledH3,
  HorizontalLine,
  TextFollow,
} from "./styled";
import { SocialLinks } from "@/components/UtilityComponents/SocialLinks";
import { useSubmitStoreContactFormMutation } from "@/generated/graphql";
import InputPhone, { PhoneInput } from "@/shared/globals/UiElements/InputPhone";
import { Card } from "@/shared/globals/UiElements/Card";
import { fireSubmitContactFormEvent } from "./utils";
import { FormApi } from "final-form";
import { AlertOctagonIcon } from "@/assets/Icons/AlertOctagonIcon";
import { CheckmarkCircleIcon } from "@/assets/Icons/CheckmarkCircleIcon";
export type ContactFormValues = {
  name: string;
  email: string;
  phone: PhoneInput;
  subjectTitle: string;
  subjectBody: string;
};

const SuccessMessage = () => (
  <div className="flex items-center bg-success-50 rounded-lg border border-success-200 p-2 gap-2xs">
    <CheckmarkCircleIcon />
    <span className="text-text-xs font-medium text-success-800">
      <FormattedMessage
        defaultMessage="We have received your message and will reach you as soon as possible."
        id="CxNsYA"
      />
    </span>
  </div>
);

const FailureMessage = () => (
  <div className="flex items-center bg-error-50 rounded-lg border border-error-200 p-2 gap-2xs">
    <AlertOctagonIcon />
    <span className="text-text-xs font-medium text-error-800">
      <FormattedMessage
        defaultMessage="Something went wrong, please try again."
        id="8GvVWZ"
      />
    </span>
  </div>
);

const Contact = () => {
  const intl = useIntl();
  const { storeId, socialMedia, contactInfo, location } = useStore();
  const [isSuccess, setIsSuccess] = React.useState<boolean | undefined>(
    undefined
  );

  const [SubmitStoreContactForm] = useSubmitStoreContactFormMutation();

  const email = contactInfo?.email;
  const phone = contactInfo?.phone;
  const address = location?.address;

  const handleSubmitContact = async (
    input: ContactFormValues,
    form: FormApi<ContactFormValues>
  ) => {
    try {
      const { data } = await SubmitStoreContactForm({
        variables: {
          input: {
            storeId,
            name: input.name,
            fromEmail: input.email,
            phone: input?.phone?.value!,
            messageSubject: input.subjectTitle,
            messageBody: input.subjectBody,
          },
        },
      });

      if (data?.submitStoreContactForm?.statusCode! < 400) {
        handleFeedback(true);
        fireSubmitContactFormEvent(input);
        form.reset();
        form.resetFieldState("name");
        form.resetFieldState("email");
        form.resetFieldState("phone");
        form.resetFieldState("subjectTitle");
        form.resetFieldState("subjectBody");
      } else {
        handleFeedback(false);
      }
    } catch (error) {
      handleFeedback(false);
    }
  };

  const handleFeedback = (value: boolean) => {
    setIsSuccess(value);
  };

  const isContactInfoExist = !!(email || phone || address);
  const socialLinkContent = socialMedia?.length ? (
    <>
      <HorizontalLine />
      <TextFollow>
        <FormattedMessage
          defaultMessage="You also follow & contact us through"
          id="13yn26"
        />
      </TextFollow>
      <SocialLinks socialSettings={socialMedia} />
    </>
  ) : null;

  return (
    <Container alignItems="stretch">
      <Card paddingSize="xl">
        <StyledH4>
          <FormattedMessage
            defaultMessage="You can send your questions by filling out the form below & We will get back to you as soon as possible."
            id="STHKir"
          />
        </StyledH4>
        <StyledSection alignItems="flex-start" isColumn={!isContactInfoExist}>
          <ContactForm>
            <Form<ContactFormValues>
              onSubmit={(values, form) => handleSubmitContact(values, form)}
              validate={validate}
              initialValues={{}}
              render={({ handleSubmit, submitting }) => {
                return (
                  <form className="w-full" onSubmit={handleSubmit}>
                    <Label>
                      <FlexRow>
                        <FormattedMessage defaultMessage="Name" id="HAlOn1" />
                        <Asterisk />
                      </FlexRow>
                      <Field name="name">
                        {({ input, meta: { error, touched } }) => (
                          <>
                            <Input
                              {...input}
                              className={error && touched ? "invalid" : ""}
                            />
                            {error && touched && (
                              <RequiredSpan>{error}</RequiredSpan>
                            )}
                          </>
                        )}
                      </Field>
                    </Label>
                    <Label>
                      <FlexRow>
                        <FormattedMessage
                          defaultMessage="Your e-mail"
                          id="mSobaU"
                        />
                        <Asterisk />
                      </FlexRow>
                      <Field name="email">
                        {({ input, meta: { error, touched } }) => (
                          <>
                            <Input
                              {...input}
                              className={error && touched ? "invalid" : ""}
                              type="email"
                            />
                            {error && touched && (
                              <RequiredSpan>{error}</RequiredSpan>
                            )}
                          </>
                        )}
                      </Field>
                    </Label>
                    <Label>
                      <FlexRow>
                        <FormattedMessage
                          defaultMessage="Phone no."
                          id="1SphRN"
                        />
                        <Asterisk />
                      </FlexRow>
                      <Field<PhoneInput> name="phone">
                        {(fieldProps) => (
                          <InputPhone
                            {...fieldProps}
                            dataTest="type-primary-phone"
                          />
                        )}
                      </Field>
                    </Label>
                    <Label>
                      <FlexRow>
                        <FormattedMessage
                          defaultMessage="Subject Title"
                          id="6N306A"
                        />
                        <Asterisk />
                      </FlexRow>
                      <Field name="subjectTitle">
                        {({ input, meta: { error, touched } }) => (
                          <>
                            <Input
                              {...input}
                              className={error && touched ? "invalid" : ""}
                            />
                            {error && touched && (
                              <RequiredSpan>{error}</RequiredSpan>
                            )}
                          </>
                        )}
                      </Field>
                    </Label>
                    <Label>
                      <FlexRow>
                        <FormattedMessage
                          defaultMessage="Subject Body"
                          id="JWJjnE"
                        />
                        <Asterisk />
                      </FlexRow>
                      <Field name="subjectBody">
                        {({ input, meta: { error, touched } }) => (
                          <>
                            <TextArea
                              {...input}
                              className={error && touched ? "invalid" : ""}
                              placeholder={intl.formatMessage({
                                defaultMessage: "Your message here...",
                                id: "nmmuZH",
                              })}
                            />
                            {error && touched && (
                              <RequiredSpan>{error}</RequiredSpan>
                            )}
                          </>
                        )}
                      </Field>
                    </Label>
                    <div className="flex flex-col gap-2 justify-end">
                      <PrimaryButton
                        type="submit"
                        style={{ marginTop: "1.5rem" }}
                        fullWidth
                        isLoading={submitting}
                      >
                        <FormattedMessage defaultMessage="Send" id="9WRlF4" />
                      </PrimaryButton>
                      {isSuccess === true ? (
                        <SuccessMessage />
                      ) : isSuccess === false ? (
                        <FailureMessage />
                      ) : null}
                    </div>
                  </form>
                );
              }}
            />
          </ContactForm>
          {isContactInfoExist ? (
            <ContactInfo>
              <Card paddingSize="xl">
                <AddressBook alignItems="flex-start">
                  {email && (
                    <InfoHolder>
                      <Icon>
                        <Envelope />
                      </Icon>
                      <Info alignItems="flex-start">
                        <H5>
                          <FormattedMessage
                            defaultMessage="Email"
                            id="sy+pv5"
                          />
                        </H5>
                        <StyledH3>{email}</StyledH3>
                      </Info>
                    </InfoHolder>
                  )}
                  {phone && (
                    <InfoHolder>
                      <Icon>
                        <Mobile />
                      </Icon>
                      <Info alignItems="flex-start">
                        <H5>
                          <FormattedMessage
                            defaultMessage="Phone"
                            id="O95R3Z"
                          />
                        </H5>
                        <H3>{phone}</H3>
                      </Info>
                    </InfoHolder>
                  )}
                  {address && (
                    <InfoHolder>
                      <Icon>
                        <Marker />
                      </Icon>
                      <Info alignItems="flex-start">
                        <H5>
                          <FormattedMessage
                            defaultMessage="Address"
                            id="e6Ph5+"
                          />
                        </H5>
                        <H3>{address}</H3>
                      </Info>
                    </InfoHolder>
                  )}
                  {socialLinkContent}
                </AddressBook>
              </Card>
            </ContactInfo>
          ) : (
            socialLinkContent
          )}
        </StyledSection>
      </Card>
    </Container>
  );
};

const validate = (values: ContactFormValues) => {
  const errors: Record<string, unknown> = {};

  if (!values.name?.trim()) {
    errors.name = (
      <FormattedMessage defaultMessage="Enter your full name" id="xIDmuE" />
    );
  }

  if (
    !values?.email ||
    !values?.email?.match(
      /^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/
    )
  ) {
    errors.email = (
      <FormattedMessage
        defaultMessage="Enter a valid email address"
        id="YRe93Y"
      />
    );
  }

  if (!values.phone?.isValid) {
    errors.phone = (
      <FormattedMessage
        defaultMessage="Enter a valid phone number"
        id="iydw3u"
      />
    );
  }

  if (!values.subjectTitle?.trim()) {
    errors.subjectTitle = (
      <FormattedMessage defaultMessage="Enter subject title" id="KkreSs" />
    );
  }

  if (!values.subjectBody?.trim()) {
    errors.subjectBody = (
      <FormattedMessage defaultMessage="Enter subject content" id="ND8eVa" />
    );
  }
  return errors;
};

export default Contact;
